import { inject, Injectable } from '@angular/core';
import { ApiClientService } from './api-client.service';
import { AuthService } from './auth.service';
import { ISmartphoto } from '@bdeo-platform/private-api';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { orderByType } from '../entities/types/order-by.type';
import { IFilterQuery } from '../entities/interfaces/services/filter-query.interface';
import { Smartphoto } from '@bdeo-motor/smartphoto-entities';
import { DamageEstimationRequest } from '../entities/types/damage-estimation.type';

export type CreateResponse = { success: boolean; id: string; selfadjustLink: string };
export type DamagesUpdatePayload = Omit<
  Parameters<ApiClientService['apiClient']['motor']['smartphoto']['damageSummary']['update']>[0],
  'access_token'
>;
export type EstimationResponse = Awaited<
  ReturnType<ApiClientService['apiClient']['motor']['smartphoto']['damage']['price']>
>['data']['body'];

export type GetListResponse = {
  items: Smartphoto[];
  pageSize: number;
  success: boolean;
  hasPrev: boolean;
  hasNext: boolean;
  nextCursor: { id: string; value: null | string } | null;
  aggregation: Record<string, number>;
};

export type Cursor = { id: string; value: null | string };

@Injectable({
  providedIn: 'root'
})
export class SmartphotoRepositoryService {
  private readonly apiClientService = inject(ApiClientService);
  private readonly authService = inject(AuthService);
  private readonly httpClient = inject(HttpClient);

  public async find(smartphotoId: string): Promise<ISmartphoto> {
    const response = await this.apiClientService.getApiClient().motor.smartphoto.get({
      selfAdjustId: smartphotoId,
      access_token: this.authService.getAccessToken()
    });

    const {
      data: { success, entity }
    } = response;

    if (!success) {
      throw new Error('Login call did not succeed');
    }

    return entity;
  }

  public async getList(orderBy: orderByType, asc: boolean, filterQuery?: IFilterQuery, cursor?: Cursor): Promise<GetListResponse> {
    if (filterQuery?.status?.length == 0) {
      delete filterQuery?.status;
    }

    const requestBody: Record<string, unknown> = {
      access_token: this.authService.getAccessToken(),
      orderBy: {
        field: orderBy,
        asc
      },
      query: filterQuery,
      includeCaseFields: true
    };

    // Add cursor and direction if cursor is provided (for pagination)
    if (cursor) {
      requestBody['cursor'] = cursor;
      requestBody['direction'] = 'next';
    }

    return await firstValueFrom(
      this.httpClient.post<GetListResponse>(`${environment.baseUrl}/selfadjusts`, requestBody, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      })
    );
  }

  public async create(caseID: string, customFields: Record<string, unknown>): Promise<CreateResponse> {
    if (customFields['case']) {
      delete customFields['case'];
    }

    const requestBody = {
      access_token: this.authService.getAccessToken(),
      // TO DO: Recuperar idioma de la compañía y sino utilizar el del navegador
      lang: 'es-ES',
      entity: {
        case_id: caseID,
        assigned_agent_id: this.authService.getUserID(),
        notificationChannel: 'none',
        ...customFields
      }
    };

    const requestHeaders = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    return firstValueFrom(this.httpClient.post<CreateResponse>(`${environment.baseUrl}/selfadjust-create`, requestBody, requestHeaders));
  }

  public async updateDamages(payload: DamagesUpdatePayload): Promise<boolean> {
    const requestBody: DamagesUpdatePayload & { access_token: string } = {
      ...payload,
      access_token: this.authService.getAccessToken(),
      AIModulesModified: ['damageSummary']
    };

    const response = await this.apiClientService.getApiClient().motor.smartphoto.damageSummary.update(requestBody);

    if (!response.data.success || !response.ok) {
      throw new Error('Damage update call did not succeed');
    }

    return response.data.waitingForAI;
  }

  public async updateBasket(smartphotoId: string, basket: string): Promise<void> {
    const response = await this.apiClientService.getMotorSmartphotosApiClient().smartphotoId.updateBasketing(
      smartphotoId,
      {
        new_basket: basket
      },
      {
        headers: {
          access_token: this.authService.getAccessToken()
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to update basket');
    }
  }

  public async updateStatus(smartphotoId: string, status: number, fields2Update?: Record<string, unknown>): Promise<void> {
    const response = await this.apiClientService.getApiClient().motor.smartphoto.update({
      selfAdjustId: smartphotoId,
      access_token: this.authService.getAccessToken(),
      fields2Update,
      status
    });

    if (!response.ok || !response.data.success) {
      throw new Error('Update call did not succeed');
    }
  }

  public async getDamageEstimation(request: DamageEstimationRequest): Promise<EstimationResponse> {
    const {
      data: { body }
    } = await this.apiClientService.getApiClient().motor.smartphoto.damage.price(request);

    return body;
  }
}
